import { computed, defineComponent, nextTick, onMounted, PropType, ref } from "vue";
import StepBox from "../../components/StepBox";
import Tracking from "../../utils/Tracking";
import FormInfo from "../../components/FormInfo";
import { SmeOrderJson } from "../../utils/SmeUtils";
import InputText from "../../forms/InputText";
import Validator, { ValidationError } from "../../utils/Validator";
import Utils from "../../utils/Utils";

export default defineComponent({
    components: {
        StepBox,
        FormInfo,
        InputText,
    },

    template: `
        <StepBox class="StepFormProfile"
            :step="1"
            :currentstep="step"

            title="Dane firmy"

            sticky="auto"

            :valid="true"
            :back="true"

            :visible_edit2="true"

            @back="$emit('back')"
            @edit="$emit('goto', 1)"
            @edit2="onEdit"
            @next="onNext"
        >
            <template v-if="!edit">
                <FormInfo type="2" title="Nazwa firmy" :value="options.billing_company" />
                <FormInfo type="2" title="Numer NIP" :value="options.billing_nip" />
                <FormInfo type="2" title="Ulica" :value="billing_address" />
                <FormInfo type="2" title="Kod pocztowy" :value="options.billing_zipcode" />
                <FormInfo type="2" title="Miasto" :value="options.billing_city" />
            </template>

            <template v-if="edit">
                <InputText label='<span class="requiredfield">*</span>Nazwa firmy' v-model="form.company" :required="true" :error="errors.company" />
                <InputText label="Numer NIP" :modelValue="options.billing_nip" :disabled="true" />
                <InputText label='<span class="requiredfield">*</span>Ulica' v-model="form.street" :required="true" :error="errors.street" />
                <InputText label='<span class="requiredfield">*</span>Numer budynku' v-model="form.buildingno" :required="true" :error="errors.buildingno" />
                <InputText label="Numer lokalu" v-model="form.apartmentno" :error="errors.apartmentno" />
                <InputText label='<span class="requiredfield">*</span>Kod pocztowy' v-model="form.zipcode" :required="true" mask="00-000" :error="errors.zipcode" />
                <InputText label='<span class="requiredfield">*</span>Miasto' v-model="form.city" :required="true" :error="errors.city" />
            </template>
        </StepBox>
    `,

    emits: [
        'next',
        'back',
        'goto',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        step: { type: Number, required: true },
    },

    setup(props, ctx) {
        const sent = ref(false);
        const edit = ref(false);

        const form = ref({
            company: null as string,
            street: null as string,
            buildingno: null as string,
            apartmentno: null as string,
            zipcode: null as string,
            city: null as string,
        });

        setData();

        const errors = ref({
            company: computed(() => inputError('company', sent.value)),
            street: computed(() => inputError('street', sent.value)),
            buildingno: computed(() => inputError('buildingno', sent.value)),
            apartmentno: computed(() => inputError('apartmentno', sent.value)),
            zipcode: computed(() => inputError('zipcode', sent.value)),
            city: computed(() => inputError('city', sent.value)),
        });

        const billing_address = computed(() => {
            const r = [];
            r.push(props.options.billing_street);

            if (props.options.billing_apartmentno) {
                r.push(props.options.billing_buildingno + '/' + props.options.billing_apartmentno);
            } else {
                r.push(props.options.billing_buildingno);
            }

            return r.join(' ').trim();
        });

        function assertError(field: string, force: boolean = false) {
            field == 'company' && (force || form.value.company) && Validator.assertRequired(form.value.company);
            field == 'street' && (force || form.value.street) && Validator.assertRequired(form.value.street);
            field == 'buildingno' && (force || form.value.buildingno) && Validator.assertRequired(form.value.buildingno);
            field == 'zipcode' && (force || form.value.zipcode) && Validator.assertZipCode(form.value.zipcode);
            field == 'city' && (force || form.value.city) && Validator.assertRequired(form.value.city);
        }

        function assertValid() {
            assertError('company', true);
            assertError('street', true);
            assertError('buildingno', true);
            assertError('apartmentno', true);
            assertError('zipcode', true);
            assertError('city', true);
        }

        function inputError(field: string, force: boolean = false) {
            try {
                assertError(field, force);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        function setData() {
            form.value.company = props.options.billing_company;
            form.value.street = props.options.billing_street;
            form.value.buildingno = props.options.billing_buildingno;
            form.value.apartmentno = props.options.billing_apartmentno;

            let zipcode = props.options.billing_zipcode?.replace('-', '');

            if (zipcode) {
                zipcode = zipcode.slice(0, 2) + '-' + zipcode.slice(2);
            }

            form.value.zipcode = zipcode;

            form.value.city = props.options.billing_city;
        }

        async function onEdit() {
            setData();

            edit.value = true;
        }

        async function onNext() {
            try {
                if (edit.value) {
                    sent.value = true;

                    assertValid();

                    const r = await Utils.setCart('form.verify', {
                        form: form.value,
                    }, false);

                    edit.value = false;
                    sent.value = false;
                }

                Tracking.doEvent('b2bsme_companydata_step1');

                ctx.emit('next');
            } catch (e) {
                nextTick(() => {
                    const $first = $('.InputWidget--error, .InputCheckbox--error').first();
                    Utils.scrollToEl($first);
                });
            }
        }

        onMounted(() => {
            try {
                assertValid();
            } catch (e) {
                sent.value = true;
                edit.value = true;
            }
        });

        return {
            edit,
            form,
            errors,

            billing_address,

            onEdit,
            onNext,
        };
    },
});
